import React, { useState, useEffect, useRef, useMemo } from 'react';
import { TrendingUp, BarChart3, Target, Zap, ChevronDown } from 'lucide-react';
import { Card } from '../../ui/Card';

interface MetricData {
  crewAverage: number;
  powerRange: { min: number; max: number };
  consistency: number;
  peakPower: number;
}

interface OverviewMetricCardsProps {
  data: MetricData | null;
  loading: boolean;
  disableCollapse?: boolean; // Disable collapse functionality (e.g., in dashboard grid)
  /** Shown when hull-set filter is narrowing the summary metrics */
  filterNote?: string;
}

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBgColor: string;
  testId: string;
  ariaLabel: string;
  subtitle?: string;
  responsiveStyles?: {
    cardPadding: string;
    labelFont: string;
    valueFont: string;
    subtitleFont: string;
    iconSize: number;
  };
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  iconBgColor,
  testId,
  ariaLabel,
  subtitle,
  responsiveStyles,
}) => {
  const defaultStyles = {
    cardPadding: 'p-4',
    labelFont: 'text-sm',
    valueFont: value === 'Coming Soon' ? 'text-lg' : 'text-2xl',
    subtitleFont: 'text-xs',
    iconSize: 14,
  };
  const styles = responsiveStyles || defaultStyles;

  return (
    <Card
      variant="interactive"
      as="article"
      className={`metric-card flex items-center justify-between ${styles.cardPadding}`}
      aria-label={ariaLabel}
    >
      <div className="metric-content flex-1">
        <h3 className={`metric-label ${styles.labelFont} font-medium text-gray-600 mb-1`}>
          {label}
        </h3>
        <div
          className={`metric-value font-lexend ${
            value === 'Coming Soon'
              ? `${styles.valueFont} font-semibold text-rowing-gray-500`
              : `${styles.valueFont} font-bold text-gray-900`
          }`}
        >
          {value}
        </div>
        {subtitle && (
          <div className={`${styles.subtitleFont} text-rowing-gray-500 mt-1`}>{subtitle}</div>
        )}
      </div>
      <div
        className={`metric-icon w-12 h-12 ${iconBgColor} rounded-lg flex items-center justify-center`}
        data-testid={testId}
      >
        {React.cloneElement(icon as React.ReactElement<{ size?: number }>, {
          size: styles.iconSize,
        })}
      </div>
    </Card>
  );
};

const LoadingCard: React.FC<{ label: string }> = ({ label }) => (
  <Card className="metric-card flex items-center justify-between p-4">
    <div className="metric-content flex-1">
      <div className="metric-label text-sm font-medium text-gray-600 mb-1">{label}</div>
      <div className="metric-value text-2xl font-bold text-gray-900 font-lexend">
        <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>
    <div className="metric-icon w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
      <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </Card>
);

const OverviewMetricCards: React.FC<OverviewMetricCardsProps> = ({
  data,
  loading,
  disableCollapse = false,
  filterNote,
}) => {
  const [isExpanded, setIsExpanded] = useState(true); // Default expanded
  // Always expanded when collapse is disabled
  const effectiveIsExpanded = disableCollapse ? true : isExpanded;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Track container width for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate responsive styles based on container width
  const responsiveStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        contentPadding: 'px-2 pb-3',
        gridGap: 'gap-2',
        cardPadding: 'p-1.5',
        labelFont: 'text-xs',
        valueFont: 'text-lg',
        subtitleFont: 'text-[0.625rem]',
        iconSize: 12,
        headerPadding: 'p-3',
        headerTitleFont: 'text-base',
        headerSubtitleFont: 'text-xs',
      };
    }
    if (containerWidth < 600) {
      return {
        contentPadding: 'px-2.5 pb-4',
        gridGap: 'gap-2.5',
        cardPadding: 'p-2',
        labelFont: 'text-xs',
        valueFont: 'text-xl',
        subtitleFont: 'text-xs',
        iconSize: 13,
        headerPadding: 'p-3',
        headerTitleFont: 'text-base',
        headerSubtitleFont: 'text-xs',
      };
    }
    if (containerWidth < 800) {
      return {
        contentPadding: 'px-3 pb-5',
        gridGap: 'gap-3',
        cardPadding: 'p-2.5',
        labelFont: 'text-xs',
        valueFont: 'text-xl',
        subtitleFont: 'text-xs',
        iconSize: 14,
        headerPadding: 'p-4',
        headerTitleFont: 'text-lg',
        headerSubtitleFont: 'text-sm',
      };
    }
    // Default for larger containers
    return {
      contentPadding: 'px-4 pb-6',
      gridGap: 'gap-4',
      cardPadding: 'p-4',
      labelFont: 'text-sm',
      valueFont: 'text-2xl',
      subtitleFont: 'text-xs',
      iconSize: 14,
      headerPadding: 'p-4',
      headerTitleFont: 'text-lg',
      headerSubtitleFont: 'text-sm',
    };
  }, [containerWidth]);

  if (loading) {
    return (
      <div ref={containerRef} className="bg-white border border-gray-200 rounded-lg mb-6">
        <div
          className={`${responsiveStyles.headerPadding} ${!disableCollapse ? 'cursor-pointer' : ''}`}
          onClick={!disableCollapse ? () => setIsExpanded(!isExpanded) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <h3 className={`${responsiveStyles.headerTitleFont} font-semibold text-gray-900`}>
                  Crew Performance
                </h3>
                {filterNote && (
                  <p className={`${responsiveStyles.headerSubtitleFont} text-rowing-blue mt-0.5`}>
                    {filterNote}
                  </p>
                )}
              </div>
              {!disableCollapse && (
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                    isExpanded ? 'transform rotate-180' : ''
                  }`}
                />
              )}
            </div>
            <div className={responsiveStyles.headerSubtitleFont + ' text-gray-600'}>
              Team balance and power distribution
            </div>
          </div>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className={responsiveStyles.contentPadding}>
            <div
              className={`overview-grid grid ${responsiveStyles.gridGap}`}
              style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              }}
            >
              <LoadingCard label="Crew Average" />
              <LoadingCard label="Power Range" />
              <LoadingCard label="Consistency" />
              <LoadingCard label="Peak Power" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div
          className={`p-4 ${!disableCollapse ? 'cursor-pointer' : ''}`}
          onClick={!disableCollapse ? () => setIsExpanded(!isExpanded) : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold text-gray-900">Crew Performance</h3>
              {!disableCollapse && (
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                    isExpanded ? 'transform rotate-180' : ''
                  }`}
                />
              )}
            </div>
            <div className="text-sm text-gray-600">Team balance and power distribution</div>
          </div>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 pb-6">
            <div className="text-center py-8 text-gray-500">
              No data available for power metrics
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white border border-gray-200 rounded-lg mb-6">
      {/* Collapsible Header */}
      <div
        className={`${responsiveStyles.headerPadding} ${!disableCollapse ? 'cursor-pointer' : ''}`}
        onClick={!disableCollapse ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h3 className={`${responsiveStyles.headerTitleFont} font-semibold text-gray-900`}>
                Crew Performance
              </h3>
              {filterNote && (
                <p className={`${responsiveStyles.headerSubtitleFont} text-rowing-blue mt-0.5`}>
                  {filterNote}
                </p>
              )}
            </div>
            {!disableCollapse && (
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                  isExpanded ? 'transform rotate-180' : ''
                }`}
              />
            )}
          </div>
          <div className={responsiveStyles.headerSubtitleFont + ' text-gray-600'}>
            Team balance and power distribution
          </div>
        </div>
      </div>
      {/* Collapsible Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className={responsiveStyles.contentPadding}>
          <div
            className={`overview-grid grid ${responsiveStyles.gridGap}`}
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            }}
            data-testid="overview-grid"
          >
            <MetricCard
              label="Crew Average"
              value={`${data.crewAverage}W`}
              icon={<TrendingUp size={responsiveStyles.iconSize} />}
              iconBgColor="bg-gray-50"
              testId="metric-icon-crew-average"
              ariaLabel={`Crew average power: ${data.crewAverage} watts`}
              responsiveStyles={responsiveStyles}
            />

            <MetricCard
              label="Power Range"
              value={`${data.powerRange.min}-${data.powerRange.max}W`}
              icon={<BarChart3 size={responsiveStyles.iconSize} />}
              iconBgColor="bg-gray-50"
              testId="metric-icon-power-range"
              ariaLabel={`Power range: ${data.powerRange.min} to ${data.powerRange.max} watts`}
              responsiveStyles={responsiveStyles}
            />

            <MetricCard
              label="Consistency"
              value="Coming Soon"
              icon={<Target size={responsiveStyles.iconSize} />}
              iconBgColor="bg-gray-50"
              testId="metric-icon-consistency"
              ariaLabel="Consistency analysis coming soon"
              subtitle="enhanced analytics"
              responsiveStyles={responsiveStyles}
            />

            <MetricCard
              label="Peak Power"
              value={`${data.peakPower}W`}
              icon={<Zap size={responsiveStyles.iconSize} />}
              iconBgColor="bg-gray-50"
              testId="metric-icon-peak-power"
              ariaLabel={`Peak power achieved: ${data.peakPower} watts`}
              responsiveStyles={responsiveStyles}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewMetricCards;
